import { Theme, css } from '@emotion/react';

import NextImage from 'next/image';
import { useRouter } from 'next/router';

import { useEffect, useState } from 'react';

import { Spacing } from 'chatie-design-system';
import { Bell, Heart, Icon, Search } from 'chatie-design-system/icon';

import isoLogo from '@public/icons/chatie_origin_logo.png';
import cheming_store from '@public/icons/cheming/ic_cheming_store.svg';
import cheming_stroke_face from '@public/icons/cheming/ic_cheming_stroke_face.svg';
import ic_free_message_badge from '@public/icons/header/ic_freeballoons.svg';
import ic_roulette from '@public/icons/ic_roulette.svg';
import play_iso_png from '@public/icons/play_iso_logo.png';
import stroke_face from '@public/icons/stroke-face.svg';
import cheming_logo from '@public/images/cheming/cheming_logo.png';
import ic_free_balloon from '@public/images/homeHeader/ic_free_balloon.svg';
import ic_notice from '@public/images/homeHeader/ic_notice.svg';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { media, responsiveMaxWidth, safeArea } from '@styles/css';
import { useAnalytics } from '@utils/analytics';
import { ParsedStorage } from '@utils/localStorage';
import { checkRequiredAppVersion, openDeepLink } from '@utils/misc';
import { useWebView } from '@utils/webview';

import SvgIcon from './SvgIcon';
import Tab, { ITab } from './Tab';

const headerWrapperCSS = (theme: Theme) =>
  css`
    background-color: ${theme.var.white};
    width: 100%;
    ${responsiveMaxWidth};
    padding-top: 10px;
    position: fixed;
    z-index: 98;
    top: 0;

    .home-header {
      display: flex;
      align-items: center;
      max-width: inherit;
      top: 0;
      z-index: 9;
      width: 100%;
      background-color: ${theme.var.white};
      justify-content: space-between;
      padding: ${Spacing.medium}px;
      height: 9%;
      padding-left: 14px;

      ${safeArea('top', 'margin')}
    }
    .logo-container {
      width: 164px;
      height: 25px;

      img {
        transition: filter 0.2s ease;
        ${theme.var.white !== '#ffffff'
          ? 'filter: brightness(0) invert(1);'
          : ''}
      }
    }

    .free_message_badge {
      position: absolute !important;
      padding: 5px;
      bottom: 13px;
      left: auto;
      right: 44px;

      z-index: 100;
    }

    .has-sidebar & {
      ${media.tablet} {
        border-left: 1px solid ${theme.var.gray200};
      }
    }
  `;

const iconsCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: ${Spacing.medium}px;
  color: ${theme.var.black};

  i svg [stroke='black'],
  i svg [stroke='#000'],
  i svg [stroke='#000000'] {
    stroke: ${theme.var.black};
  }
  i svg [fill='#444444'],
  i svg [fill='#444'] {
    fill: ${theme.var.gray800};
  }
  i svg [fill='#A6DADF'] {
    fill: ${theme.name === 'dark' ? theme.var.gray300 : '#A6DADF'};
  }
  .balloon-icon i svg [fill='#EF4239'] {
    fill: ${theme.name === 'dark' ? theme.var.gray500 : '#EF4239'};
  }
  .balloon-icon i svg [fill='#FFB21C'] {
    fill: ${theme.name === 'dark' ? theme.var.gray400 : '#FFB21C'};
  }
`;

const iconButtonStyle = {
  cursor: 'pointer',
  fontSize: 18,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  background: 'none',
  border: 'none',
  padding: 0,
} as const;

type HomeHeaderProps<T> = {
  hasNewNotification: boolean;
  isHome?: boolean;
  tabs?: {
    tabs: ITab<T>[];
    activeTab: T;
    onChange: (tab: T) => void;
  };

  lastPeekTabViewDate?: Date;
};

const HomeHeader = <T extends string | number>({
  hasNewNotification,
  tabs,
  lastPeekTabViewDate,
  isHome = false,
}: HomeHeaderProps<T>) => {
  const router = useRouter();
  const isProduction = process.env.NEXT_PUBLIC_ENV === 'production';
  const isStagingOrDev =
    process.env.NEXT_PUBLIC_ENV === 'staging' ||
    process.env.NEXT_PUBLIC_ENV === 'dev';
  const { platform, version, isCheming } = useGlobalContext();
  const { firebase } = useAnalytics();
  const { web } = useWebView();
  const [isShowFreeMessageBadge, setIsShowFreeMessageBadge] = useState(false);

  const handleRouting = (path: string) => {
    if (path === '/payment') {
      if (platform === Platform.web) {
        router.push(path);
      }
      if (
        checkRequiredAppVersion(platform, version, {
          iOS: '2.26.1',
          android: '2.32.4',
        })
      ) {
        return web.openPage('/payment');
      } else {
        return openDeepLink('chatie-app://payment', router);
      }
    }
    if (platform === Platform.web) {
      router.push(path);
    } else {
      web.openPage(path);
    }
  };

  const handleClickBadge = () => {
    const currentDate = new Date().getTime();
    ParsedStorage.setItem('freeMessageBadgeClosedDate', currentDate);
    setIsShowFreeMessageBadge(false);

    return;
  };

  const handleClickEventPage = () => {
    firebase.logEvent('event_page_click', {});
    handleRouting('/events');
  };

  useEffect(() => {
    const targetDate = new Date('2024-11-01').getTime();
    const currentTime = new Date().getTime();

    if (currentTime > targetDate) {
      setIsShowFreeMessageBadge(false);
      return;
    }

    const storedDate = ParsedStorage.getItem('freeMessageBadgeClosedDate');

    if (storedDate) {
      const currentTime = new Date().getTime();
      const timeDifference = currentTime - storedDate;

      const oneDayInMilliseconds = 24 * 60 * 60 * 1000;

      if (timeDifference < oneDayInMilliseconds) {
        setIsShowFreeMessageBadge(false);
        return;
      }
    }

    setIsShowFreeMessageBadge(true);
  }, []);

  return (
    <div css={headerWrapperCSS} suppressHydrationWarning={true}>
      <header>
        <div className="home-header">
          <div className="logo-container">
            {isCheming ? (
              <NextImage
                src={cheming_logo}
                loading="eager"
                objectFit={'cover'}
                width={60}
                height={22}
                alt={'logo'}
                unoptimized
              />
            ) : (
              <>
                <NextImage
                  src={isoLogo}
                  loader={({ src }) => src}
                  loading="eager"
                  objectFit={'cover'}
                  width={120}
                  height={25}
                  alt={'logo'}
                />
                {!isHome && (
                  <NextImage
                    src={play_iso_png}
                    loading="eager"
                    objectFit={'cover'}
                    width={40}
                    height={25}
                    alt={'logo'}
                    unoptimized
                  />
                )}
              </>
            )}
          </div>
          <div css={iconsCSS}>
            {!isProduction && (
              <div
                onClick={() => handleRouting('/millie-test')}
                className={'face-container'}
              >
                <SvgIcon icon={Heart} />
              </div>
            )}

            {isStagingOrDev && (
              <button
                onClick={() => handleRouting('/cutscene-verify')}
                title="컷씬 검증 (staging · dev)"
                type="button"
                style={iconButtonStyle}
                aria-label="cutscene-verify"
              >
                🎬
              </button>
            )}

            {isCheming ? (
              <>
                <div onClick={() => handleRouting('/search')}>
                  <Icon icon={Search} />
                </div>
                <div
                  onClick={handleClickEventPage}
                  className={'face-container'}
                >
                  <SvgIcon icon={cheming_stroke_face} />
                </div>
                <div onClick={() => handleRouting('/payment')}>
                  <SvgIcon icon={cheming_store} />
                </div>
              </>
            ) : (
              <>
                <div onClick={() => handleRouting('/search')}>
                  <Icon icon={Search} />
                </div>
                <div
                  onClick={handleClickEventPage}
                  className={'face-container'}
                >
                  <SvgIcon icon={stroke_face} />
                </div>

                {process.env.NEXT_PUBLIC_FEATURE_ROULETTE === 'true' && (
                  <div onClick={() => handleRouting('/roulette')}>
                    <SvgIcon icon={ic_roulette} />
                  </div>
                )}
                <div
                  onClick={() => handleRouting('/payment')}
                  className="balloon-icon"
                >
                  <SvgIcon icon={ic_free_balloon} />
                </div>
                <div onClick={() => handleRouting('/notifications')}>
                  {hasNewNotification ? (
                    <SvgIcon icon={ic_notice} />
                  ) : (
                    <Icon icon={Bell} />
                  )}
                </div>
              </>
            )}
          </div>
          {isShowFreeMessageBadge && (
            <div className="free_message_badge" onClick={handleClickBadge}>
              <SvgIcon icon={ic_free_message_badge} />
            </div>
          )}
        </div>
        {!isCheming && tabs && (
          <Tab {...tabs} fit lastPeekTabViewDate={lastPeekTabViewDate} />
        )}
      </header>
    </div>
  );
};

export default HomeHeader;
